import { home } from "../constants/actionTypes";
import { sortBySponsorAndDate } from "../helpers";

const defaultState = {
  dashboardData: {
    listing_messages: [],
    leads: [],
    calls: [],
    notifications: [],
    qa: [],
    cases: [],
    listings: []
  },
  error: {
    listingMessages: null,
    dashboardData: null,
    favorites: false
  },
  preloaders: {
    dashboard: false,
    listingAssistant: false,
    addNewListingData: false,
    saved: false,
    getFollowing: false,
    removeFollowing: false,
    feedStream: false,
    feedStreamMore: false,
    savedFeed: false,
    savedFeedMore: false,
    upcomingEvents: false
  },
  // GROWTH-7129: structured errors.<section>, per the React dashboard standard's new-slice
  // convention — kept separate from the legacy `error` object above (one-way ratchet).
  errors: {
    feedStream: null,
    feedStreamMore: null,
    feedSeen: null,
    savedFeed: null,
    savedFeedMore: null,
    feedMessage: null,
    upcomingEvents: null
  },
  successListingAssistant: false,
  addNewListingData: {},
  savedData: {
    list: {},
    count: {}
  },
  myFeed: {
    list: {},
    count: {}
  },
  feedPagination: {},
  followings: [],
  // GET /api-v2/feed (GROWTH-7127/7128) behind FEED_V2_ENABLED.
  feedStream: {
    items: [],
    nextCursor: null,
    lastSeenAt: null,
    newCount: 0,
    // GROWTH-7130: {community_id: count}, keyed by the member's specific followed communities
    // (never the "all communities" wildcard) — the sidebar's per-row counts.
    newByCommunity: {},
    // GROWTH-7573: {profile, organization, event, job} for the current community scope (the type
    // filter ignored) and the filtered result's total — null while the API doesn't send them.
    typeCounts: null,
    total: null,
    // GROWTH-7860: the most the list pages (the API sends it only when total is over it) — null otherwise.
    listLimit: null
  },
  // GET /api-v2/feed/saved (GROWTH-7140), the Saved tab in feed-card shape.
  savedFeed: {
    items: [],
    nextCursor: null
  },
  // When the member last saved/unsaved a listing (ms); FeedStream sends it as `fresh` so the
  // browser-cached GET /api-v2/feed can't return a stale heart state.
  favoritesChangedAt: null,
  // GET /api-v2/feed/upcoming-events (GROWTH-7131), the strip above the stream.
  upcomingEvents: {
    items: []
  }
};

export default function(state = defaultState, action) {
  switch (action.type) {
    case home.SET_LOADING:
      return {
        ...state,
        preloaders: { ...state.preloaders, [action.payload.section]: action.payload.isSectionLoading }
      };

    case home.GET_DASHBOARD_DATA_SUCCESS:
      return {
        ...state,
        dashboardData: action.payload,
        preloaders: {
          dashboard: false
        }
      };

    case home.GET_DASHBOARD_DATA_FAIL:
      return {
        ...state,
        preloaders: {
          dashboard: false
        }
      };

    case home.GET_ADD_NEW_LISTING_DATA: {
      return {
        ...state,
        addNewListingData: action.payload,
        preloaders: {
          ...state.preloaders,
          addNewListingData: false
        }
      };
    }

    case home.GET_ADD_NEW_LISTING_DATA_FAIL: {
      return {
        ...state,
        preloaders: {
          ...state.preloaders,
          addNewListingData: false
        }
      };
    }

    case home.ADD_NEW_LISTING: {
      return {
        ...state,
        preloaders: { ...state.preloaders, listingAssistant: false }
      };
    }

    case home.ADD_NEW_LISTING_FAIL: {
      return {
        ...state,
        preloaders: { ...state.preloaders, listingAssistant: false }
      };
    }

    case home.GET_SAVED: {
      const events = sortBySponsorAndDate(action.payload.list.events);
      const profiles = sortBySponsorAndDate(action.payload.list.profiles);
      const jobs = sortBySponsorAndDate(action.payload.list.jobs);
      const organizations = sortBySponsorAndDate(action.payload.list.organizations);

      return {
        ...state,
        preloaders: { ...state.preloaders, saved: false },
        savedData: {
          list: { profiles, jobs, organizations, events },
          count: action.payload.count
        }
      };
    }
    case home.GET_SAVED_FAIL: {
      return {
        ...state,
        preloaders: { ...state.preloaders, saved: false }
      };
    }

    case home.GET_MY_FEED: {
      return {
        ...state,
        preloaders: { ...state.preloaders, feed: false },
        myFeed: {
          list: action.payload.list,
          count: action.payload.count
        },
        feedPagination: action.payload.pagination
      };
    }

    case home.GET_MY_FEED_FAIL: {
      return {
        ...state,
        preloaders: { ...state.preloaders, feed: false }
      };
    }

    case home.GET_FOLLOWING: {
      return {
        ...state,
        preloaders: { ...state.preloaders, feed: false },
        followings: action.payload
      };
    }

    case home.GET_FOLLOWING_FAIL: {
      return {
        ...state,
        preloaders: { ...state.preloaders, getFollowing: false }
      };
    }

    case home.REMOVE_FOLLOWING: {
      return {
        ...state,
        preloaders: { ...state.preloaders, removeFollowing: false },
        followings: state.followings.filter(item => String(item.user_following_id) !== String(action.payload))
      };
    }

    case home.ADD_FAVORITES:
    case home.REMOVE_FAVORITES: {
      return {
        ...state,
        favoritesChangedAt: action.meta?.changedAt ?? state.favoritesChangedAt
      };
    }

    case home.ADD_FAVORITES_FAIL: {
      return {
        ...state,
        error: { ...state, favorites: true }
      };
    }

    case home.REMOVE_ALL_ERROR: {
      return {
        ...state,
        error: { ...state.error }
      };
    }

    case home.REMOVE_FOLLOWING_FAIL: {
      return {
        ...state,
        preloaders: { ...state.preloaders, removeFollowing: false },
        error: { ...state, favorites: true }
      };
    }

    case home.GET_FEED_STREAM: {
      const {
        items,
        nextCursor,
        lastSeenAt,
        newCount,
        newByCommunity,
        typeCounts = null,
        total = null,
        listLimit = null,
        append,
        section
      } = action.payload;
      // A "Show older" page can repeat a card already listed (the server keeps one order per cursor, but a
      // cursor it no longer holds ranks live); a repeated id would render twice under one React key.
      const listedIds = new Set(state.feedStream.items.map(item => item.id));

      return {
        ...state,
        preloaders: { ...state.preloaders, [section]: false },
        // A new first page also drops the previous list's "Show older" error.
        errors: { ...state.errors, [section]: null, ...(append ? {} : { feedStreamMore: null }) },
        feedStream: {
          items: append ? [...state.feedStream.items, ...items.filter(item => !listedIds.has(item.id))] : items,
          nextCursor,
          // A "Show older" page re-reads last_seen_at/new_count/new_by_community too, but the
          // header/sidebar shouldn't shift value while the member is paginating — only a fresh
          // (non-append) load updates them.
          lastSeenAt: append ? state.feedStream.lastSeenAt : lastSeenAt,
          newCount: append ? state.feedStream.newCount : newCount,
          newByCommunity: append ? state.feedStream.newByCommunity : newByCommunity || {},
          typeCounts: append ? state.feedStream.typeCounts : typeCounts,
          total: append ? state.feedStream.total : total,
          listLimit: append ? state.feedStream.listLimit : listLimit
        }
      };
    }

    case home.GET_FEED_STREAM_FAIL: {
      const { section, message, status } = action.payload;

      return {
        ...state,
        preloaders: { ...state.preloaders, [section]: false },
        errors: { ...state.errors, [section]: { message, status } },
        // A failed first page (a new type filter) must not leave the previous filter's cards
        // under it; a failed "Show older" keeps what is already loaded.
        feedStream: section === "feedStream" ? { ...state.feedStream, items: [], nextCursor: null } : state.feedStream
      };
    }

    case home.GET_UPCOMING_EVENTS: {
      return {
        ...state,
        preloaders: { ...state.preloaders, upcomingEvents: false },
        errors: { ...state.errors, upcomingEvents: null },
        upcomingEvents: { items: action.payload.items }
      };
    }

    case home.GET_UPCOMING_EVENTS_FAIL: {
      const { section, message, status } = action.payload;

      return {
        ...state,
        preloaders: { ...state.preloaders, [section]: false },
        errors: { ...state.errors, [section]: { message, status } },
        upcomingEvents: { items: [] }
      };
    }

    case home.MARK_FEED_SEEN_FAIL: {
      const { section, message, status } = action.payload;

      return {
        ...state,
        errors: { ...state.errors, [section]: { message, status } }
      };
    }

    case home.GET_SAVED_FEED: {
      const { items, nextCursor, append, section } = action.payload;

      return {
        ...state,
        preloaders: { ...state.preloaders, [section]: false },
        errors: { ...state.errors, [section]: null, ...(append ? {} : { savedFeedMore: null }) },
        savedFeed: {
          items: append ? [...state.savedFeed.items, ...items] : items,
          nextCursor
        }
      };
    }

    case home.GET_SAVED_FEED_FAIL: {
      const { section, message, status } = action.payload;

      return {
        ...state,
        preloaders: { ...state.preloaders, [section]: false },
        errors: { ...state.errors, [section]: { message, status } },
        savedFeed: section === "savedFeed" ? { ...state.savedFeed, items: [], nextCursor: null } : state.savedFeed
      };
    }

    case home.REMOVE_SAVED_FEED_ITEM: {
      return {
        ...state,
        savedFeed: {
          ...state.savedFeed,
          items: state.savedFeed.items.filter(item => item.id !== action.payload)
        }
      };
    }

    case home.SEND_FEED_MESSAGE: {
      return {
        ...state,
        errors: { ...state.errors, feedMessage: null }
      };
    }

    case home.SEND_FEED_MESSAGE_FAIL: {
      const { section, message, status } = action.payload;

      return {
        ...state,
        errors: { ...state.errors, [section]: { message, status } }
      };
    }

    default:
      return state;
  }
}
